import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { authApi } from '../services/api';
import appConfig from '../client_config.js';

// 用户类型定义
interface User {
  id: number;
  username: string;
  isAdmin: boolean;
}

// 认证上下文状态类型
interface AuthContextType {
  currentUser: User | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  login: (username: string, password: string) => Promise<void>;
  register: (username: string, password: string, inviteCode: string) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
  error: string | null;
}

// 创建认证上下文
const AuthContext = createContext<AuthContextType | null>(null);

// 提供者组件Props
interface AuthProviderProps {
  children: ReactNode;
}

// 认证提供者组件
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // 检查用户是否已认证
  const isAuthenticated = !!currentUser;
  
  // 检查用户是否为管理员
  const isAdmin = currentUser?.isAdmin || false;

  // 初始化时加载当前用户
  useEffect(() => {
    const loadUser = async () => {
      // 检查是否有保存的token
      const token = localStorage.getItem(appConfig.auth.tokenKey);
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        // 尝试获取当前用户信息
        const response: any = await authApi.getCurrentUser();
        console.log('获取当前用户成功:', response.user);
        
        if (!response.user || !response.user.id) {
          console.error('获取的用户信息不完整:', response);
          localStorage.removeItem(appConfig.auth.tokenKey);
          localStorage.removeItem(appConfig.auth.userKey);
          setIsLoading(false);
          return;
        }
        
        setCurrentUser(response.user);
      } catch (err) {
        console.error('Failed to load user', err);
        localStorage.removeItem(appConfig.auth.tokenKey);
        localStorage.removeItem(appConfig.auth.userKey);
      } finally {
        setIsLoading(false);
      }
    };

    loadUser();
  }, []);

  // 用户登录
  const login = async (username: string, password: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response: any = await authApi.login({ username, password });
      console.log('登录成功，用户信息:', response.user);
      
      if (!response.user || !response.user.id) {
        throw new Error('服务器返回的用户信息不完整');
      }
      
      // 保存令牌和用户信息
      localStorage.setItem(appConfig.auth.tokenKey, response.token);
      localStorage.setItem(appConfig.auth.userKey, JSON.stringify(response.user));
      
      setCurrentUser(response.user);
    } catch (err: any) {
      console.error('AuthContext登录失败:', err);
      
      // 确保错误信息正确传递
      let errorMessage = '登录失败';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.errors && err.response.data.errors.length > 0) {
        errorMessage = err.response.data.errors[0].msg;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      
      // 重新抛出错误，确保Login组件能够捕获
      const enhancedError = new Error(errorMessage) as any;
      enhancedError.response = err.response;
      throw enhancedError;
    } finally {
      setIsLoading(false);
    }
  };

  // 用户注册
  const register = async (username: string, password: string, inviteCode: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response: any = await authApi.register({ username, password, inviteCode });
      
      // 保存令牌和用户信息
      localStorage.setItem(appConfig.auth.tokenKey, response.token);
      localStorage.setItem(appConfig.auth.userKey, JSON.stringify(response.user));
      
      setCurrentUser(response.user);
    } catch (err: any) {
      setError(err.response?.data?.message || '注册失败');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // 用户登出
  const logout = () => {
    localStorage.removeItem(appConfig.auth.tokenKey);
    localStorage.removeItem(appConfig.auth.userKey);
    setCurrentUser(null);
  };

  const value = {
    currentUser,
    isAuthenticated,
    isAdmin,
    login,
    register,
    logout,
    isLoading,
    error
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// 自定义Hook，方便获取认证上下文
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;